import React from 'react'
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  CircularProgress
} from '@mui/material'
import { Send as SendIcon, Email, Phone, LocationOn } from '@mui/icons-material'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useToast } from '../../hooks/useToast'
import { useToastMessages } from '../../shared/utils/toastMessages'
import SEOHead from '../../shared/components/SEOHead'

interface ContactFormData {
  name: string
  email: string
  subject: string
  message: string
}

const Contact: React.FC = () => {
  const { t } = useTranslation()
  const { toastPromise } = useToast()
  const toastMessages = useToastMessages()
  const [submitStatus, setSubmitStatus] = React.useState<'idle' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = React.useState<string>('')

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<ContactFormData>({
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      message: ''
    }
  })

  const onSubmit = async (data: ContactFormData) => {
    setSubmitStatus('idle')
    setErrorMessage('')

    const promise = fetch('/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to send message' }))
        throw new Error(errorData.message || `Error: ${response.status}`)
      }
      return response
    })

    toastPromise(promise, {
      loading: toastMessages.sending(),
      success: () => {
        setSubmitStatus('success')
        reset()
        setTimeout(() => {
          setSubmitStatus('idle')
        }, 5000)
        return toastMessages.messageSent()
      },
      error: (error) => {
        setSubmitStatus('error')
        const message = error instanceof Error ? error.message : toastMessages.messageError()
        setErrorMessage(message)
        return message
      },
    })
  }

  return (
    <>
      <SEOHead
        title={t('contact.title', 'Contact Us')}
        description={t('contact.description', 'Get in touch with Kavithai.de. We love to hear from you!')}
      />

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, textAlign: 'center' }}>
          {t('contact.title', 'Contact Us')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 6, textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
          {t('contact.subtitle', 'Have a question or suggestion? We\'d love to hear from you. Send us a message and we\'ll respond as soon as possible.')}
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 4 }}>
          {/* Contact Form */}
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              {t('contact.form.title', 'Send us a Message')}
            </Typography>

            {submitStatus === 'success' && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {t('contact.form.success', 'Thank you for your message! We\'ll get back to you soon.')}
              </Alert>
            )}

            {submitStatus === 'error' && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {errorMessage || t('contact.form.error', 'Failed to send message. Please try again.')}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label={t('contact.form.name', 'Name')}
                  {...register('name', {
                    required: t('contact.form.nameRequired', 'Name is required'),
                    minLength: { value: 2, message: t('contact.form.nameMinLength', 'Name must be at least 2 characters') }
                  })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={isSubmitting}
                  placeholder={t('contact.form.namePlaceholder', 'Your full name')}
                />

                <TextField
                  fullWidth
                  label={t('contact.form.email', 'Email')}
                  type="email"
                  {...register('email', {
                    required: t('contact.form.emailRequired', 'Email is required'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('contact.form.emailInvalid', 'Invalid email address')
                    }
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isSubmitting}
                  placeholder={t('contact.form.emailPlaceholder', 'your.email@example.com')}
                />

                <TextField
                  fullWidth
                  label={t('contact.form.subject', 'Subject')}
                  {...register('subject', {
                    required: t('contact.form.subjectRequired', 'Subject is required'),
                    minLength: { value: 5, message: t('contact.form.subjectMinLength', 'Subject must be at least 5 characters') }
                  })}
                  error={!!errors.subject}
                  helperText={errors.subject?.message}
                  disabled={isSubmitting}
                  placeholder={t('contact.form.subjectPlaceholder', 'What is this about?')}
                />

                <TextField
                  fullWidth
                  label={t('contact.form.message', 'Message')}
                  multiline
                  rows={6}
                  {...register('message', {
                    required: t('contact.form.messageRequired', 'Message is required'),
                    minLength: { value: 10, message: t('contact.form.messageMinLength', 'Message must be at least 10 characters') }
                  })}
                  error={!!errors.message}
                  helperText={errors.message?.message}
                  disabled={isSubmitting}
                  placeholder={t('contact.form.messagePlaceholder', 'Your message here...')}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {isSubmitting ? t('contact.form.sending', 'Sending...') : t('contact.form.send', 'Send Message')}
                </Button>
              </Stack>
            </Box>
          </Paper>

          {/* Contact Information */}
          <Box>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                {t('contact.info.title', 'Contact Information')}
              </Typography>

              <Stack spacing={3}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Email sx={{ color: 'primary.main', mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {t('contact.info.email', 'Email')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      hello@kavithai.de
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Phone sx={{ color: 'primary.main', mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {t('contact.info.phone', 'Phone')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('contact.info.phoneAvailable', 'Available upon request')}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <LocationOn sx={{ color: 'primary.main', mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {t('contact.info.location', 'Location')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Germany
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Paper>

            <Paper elevation={3} sx={{ p: 4, borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                {t('contact.help.title', 'Need Help?')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t('contact.help.description', 'Check out our FAQ or browse through our poetry collection.')}
              </Typography>
              <Button
                variant="contained"
                href="/"
                sx={{
                  bgcolor: 'background.paper',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'background.default'
                  }
                }}
              >
                {t('contact.help.exploreButton', 'Explore Poems')}
              </Button>
            </Paper>
          </Box>
        </Box>
      </Container>
    </>
  )
}

export default Contact
